import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useApi } from "@/core/api";
import { useAuth } from "@/core/auth";
import { normalizePhoneWithPlusPrefix } from "@/core/utils/phone";
import { useGetMyUser } from "@/user/query";
import { businessUsersKey } from "@/user/query";
import {
  IBusiness,
  BusinessOption,
  IEnrichedBusiness,
  BusinessStats,
  SaveBankDetailsParams,
  UpdateDesiredAreaCodesParams,
  UploadUsersParams,
  UploadUsersResponse,
  IBusinessWeeklyStats,
  IBusinessTechnicianJobCounts,
  TechnicianJobCountsResponse,
  CurrentUserWorkbookAccessResponse,
} from "./business";
import { BUSINESS_QUERY_KEYS } from "./constants";
import { getEnrichedBusiness, getEnrichedBusinessWeeklyStats } from "./utils";

export const businessesKey = ["businesses"] as const;
export const businessOptionsKey = ["business-options"] as const;
export const businessKey = (businessId?: string | null) =>
  ["business", businessId] as const;
export const businessStatsKey = (businessId?: string | null) =>
  ["business-stats", businessId] as const;
export const businessWeeklyStatsKey = (businessId?: string | null) =>
  ["business-weekly-stats", businessId] as const;
export const businessTechnicianJobCountsKey = (businessId?: string | null) =>
  ["business-technician-job-counts", businessId] as const;
export const businessReviewAccessKey = (businessId?: string | null) =>
  ["business-review-access", businessId] as const;
export const currentUserWorkbookAccessKey = (
  businessId?: string | null,
  userId?: string,
) =>
  userId
    ? ([
        BUSINESS_QUERY_KEYS.currentUserWorkbookAccess,
        businessId,
        userId,
      ] as const)
    : ([BUSINESS_QUERY_KEYS.currentUserWorkbookAccess, businessId] as const);
export const currentUserWorkbookAccessPath = (businessId: string) =>
  `/businesses/${businessId}/workbook_access`;

export const useGetBusinessOptions = (options?: { enabled?: boolean }) => {
  const api = useApi();

  return useQuery({
    queryKey: businessOptionsKey,
    queryFn: (): Promise<BusinessOption[]> =>
      api
        .get<BusinessOption[]>("/businesses/options")
        .then((response) => response.data),
    enabled: options?.enabled,
  });
};

export const useGetBusinesses = <TData = IEnrichedBusiness[]>(
  select?: (data: IEnrichedBusiness[]) => TData,
) => {
  const api = useApi();
  const { isFetched } = useGetMyUser();

  return useQuery({
    queryKey: businessesKey,
    queryFn: (): Promise<IEnrichedBusiness[]> =>
      api
        .get("/businesses")
        .then((response) => response.data.map(getEnrichedBusiness)),
    select,
    enabled: isFetched,
    refetchOnWindowFocus: "always",
  });
};

export const useGetBusiness = <TData = IEnrichedBusiness>(
  businessId?: string | null,
  select?: (data: IEnrichedBusiness) => TData,
  options?: Pick<
    UseQueryOptions<IEnrichedBusiness, Error, TData>,
    "refetchInterval" | "refetchOnWindowFocus"
  >,
) => {
  const api = useApi();
  return useQuery({
    queryKey: businessKey(businessId),
    queryFn: (): Promise<IEnrichedBusiness> =>
      api
        .get(`/businesses/${businessId}`)
        .then((response) => getEnrichedBusiness(response.data)),
    // .then((data) => ({ ...data, onboarding_step: "setup" })),
    enabled: !!businessId,
    select,
    ...options,
  });
};

export const useGetCurrentUserWorkbookAccess = (
  businessId?: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<CurrentUserWorkbookAccessResponse> => {
  const api = useApi();

  const { user } = useAuth();
  return useQuery({
    queryKey: currentUserWorkbookAccessKey(businessId, user?.id),
    queryFn: ({ signal }): Promise<CurrentUserWorkbookAccessResponse> =>
      api
        .get<CurrentUserWorkbookAccessResponse>(
          currentUserWorkbookAccessPath(businessId!),
          { signal },
        )
        .then((response) => response.data),
    gcTime: 0,
    retry: false,
    refetchInterval: 30_000,
    // Access gates require a response fetched after the consuming page mounts.
    staleTime: 0,
    refetchOnMount: "always",
    enabled: !!businessId && (options?.enabled ?? true),
  });
};

export const useGetBusinessStats = (
  businessId?: string | null,
): UseQueryResult<BusinessStats> => {
  const api = useApi();
  return useQuery({
    queryKey: businessStatsKey(businessId),
    queryFn: (): Promise<BusinessStats> =>
      api
        .get(`/businesses/${businessId}/stats`)
        .then((response) => response.data),
    enabled: !!businessId,
  });
};

export const useGetBusinessWeeklyStats = (
  businessId?: string | null,
): UseQueryResult<IBusinessWeeklyStats> => {
  const api = useApi();
  return useQuery({
    queryKey: businessWeeklyStatsKey(businessId),
    queryFn: (): Promise<IBusinessWeeklyStats> =>
      api
        .get(`/businesses/${businessId}/weekly_stats`)
        .then((response) => getEnrichedBusinessWeeklyStats(response.data)),
    enabled: !!businessId,
  });
};

export const useGetBusinessTechnicianJobCounts = (
  businessId?: string | null,
  options?: Omit<
    UseQueryOptions<IBusinessTechnicianJobCounts>,
    "queryKey" | "queryFn"
  >,
): UseQueryResult<IBusinessTechnicianJobCounts> => {
  const api = useApi();
  return useQuery({
    queryKey: businessTechnicianJobCountsKey(businessId),
    queryFn: (): Promise<IBusinessTechnicianJobCounts> =>
      api
        .get<TechnicianJobCountsResponse>(
          `/businesses/${businessId}/technician_job_counts`,
          {
            headers: {
              "Cache-Control": "max-age=0",
            },
          },
        )
        .then((response) => response.data.technician_counts),
    enabled: !!businessId && (options?.enabled ?? true),
    ...options,
  });
};

/**
 * Hook for creating or updating a business's bank details
 * @param businessId The ID of the business
 * @returns Mutation function and status for saving bank details
 */
export const useSaveBankDetails = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: SaveBankDetailsParams,
    ): Promise<AxiosResponse<IBusiness>> =>
      api.put(`/businesses/${businessId}`, params),
    onSuccess: ({ data }) => {
      // Update the business query data directly with the response
      queryClient.setQueryData(
        businessKey(businessId),
        (old: IBusiness | undefined) => {
          if (!old) return data;
          return {
            ...old,
            bank_detail: data.bank_detail,
          };
        },
      );
    },
  });
};

export const useUpdateDesiredAreaCodes = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      params: UpdateDesiredAreaCodesParams,
    ): Promise<AxiosResponse<IBusiness>> =>
      api.put(`/businesses/${businessId}`, params),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(businessKey(businessId), (old: IBusiness) => ({
        ...old,
        desired_area_codes: data.desired_area_codes,
      }));
    },
  });
};

/**
 * Hook for uploading users in bulk via CSV
 * @param businessId The ID of the business
 * @returns Mutation function and status for uploading users
 */
export const useUploadUsers = (businessId: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      welcomeEmail = true,
    }: Omit<UploadUsersParams, "businessId">): Promise<UploadUsersResponse> => {
      if (!businessId) {
        throw new Error("Business ID is required");
      }

      const formData = new FormData();
      // Adding the file with explicit filename
      formData.append("file", file, file.name);
      formData.append("business_id", businessId);
      formData.append("welcome_email", welcomeEmail.toString());

      const response = await api.post("/users", formData);

      return response.data;
    },
    onSuccess: () => {
      // Invalidate users query to refresh the list after upload
      queryClient.invalidateQueries({ queryKey: businessUsersKey(businessId) });
    },
  });
};

/**
 * Hook for saving general business details
 * @param businessId The ID of the business
 * @returns Mutation function and status for saving business details
 */
export const useSaveBusiness = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newBusiness: Partial<IBusiness>) => {
      const preparedBusiness = {
        ...newBusiness,
        phone_number: newBusiness.phone_number
          ? normalizePhoneWithPlusPrefix(newBusiness.phone_number)
          : undefined,
      };

      return api
        .put(`/businesses/${businessId}`, preparedBusiness)
        .then((res) => res.data);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(businessKey(businessId), (old?: IBusiness) =>
        old ? { ...old, ...data } : data,
      );
      queryClient.setQueryData<IBusiness[]>(
        businessesKey,
        (businesses: IBusiness[] | undefined) =>
          businesses?.map((b) => (b.id === businessId ? { ...b, ...data } : b)),
      );
    },
  });
};

export const useGetBusinessReviewAccess = (
  businessId?: string | null,
): UseQueryResult<{ has_review_access: boolean }> => {
  const api = useApi();
  return useQuery({
    queryKey: businessReviewAccessKey(businessId),
    queryFn: () =>
      api
        .get(`/businesses/${businessId}/has_review_access`)
        .then((response) => response.data),
    enabled: !!businessId,
  });
};
